import { Badge, Chip, IconButton, Stack, Typography } from '@mui/material';
import { Link, Trash2, Copy } from 'lucide-react';

const BADGE_BASE_STYLE = {
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "10px"
};

const TEXT_BASE_STYLE = {
    color: "#000000",
    fontSize: "10px"
};

const STATUS_COLOR_MAP = {
    "P": "#0D6EFD",
    "A": "#ffc107",
    "W": "#0DCAF0",
    "S": "#198754",
    "R": "#DC3545",
    "F": "#DC3545"
}

export const getProfileColumns = ({
    isVendor,
    onDelete,
    onCopy,
    onViewChildLinks,
    isAdmin,
    isSuperUser
}) => [
        {
            field: "date",
            headerName: "Date",
            width: 110,
            renderCell: (params) => {
                const _id = params.row?._id;

                return (
                    <Badge
                        sx={{
                            ...BADGE_BASE_STYLE,
                            backgroundColor: "#6c757d",
                            color: "#FFFFFF"
                        }}
                    >
                        {new Date(parseInt(_id.substring(0, 8), 16) * 1000).toLocaleDateString("en-IN")}
                    </Badge>
                )
            }
        },

        {
            field: "campaignName",
            headerName: "Campaign",
            flex: 1,
            minWidth: 180,
            valueGetter: (params) => params.row.campaign?.name || "",
            renderCell: (params) => {
                const campaign = params.row?.campaign;

                return (
                    <Typography
                        sx={{
                            ...TEXT_BASE_STYLE
                        }}
                    >
                        {campaign?.name}
                    </Typography>
                )
            }
        },

        {
            field: "platformNames",
            headerName: "Platform",
            width: 140,
            renderCell: (params) => {
                const platforms = params.row?.platform;

                return (
                    <Typography
                        sx={TEXT_BASE_STYLE}
                    >
                        {platforms?.map(p => p.name).join(' ')}
                    </Typography>
                )
            }
        },

        {
            field: "influencerName",
            headerName: "Influencer",
            width: 140,
            valueGetter: (params) => params.row?.influencer?.username,
            renderCell: (params) => {
                const influencer = params.row?.influencer;

                return (
                    influencer ?
                        <Badge
                            sx={{
                                ...BADGE_BASE_STYLE,
                                color: "#FFFFFF",
                                background: STATUS_COLOR_MAP.P
                            }}
                        >
                            {influencer?.username || influencer?.name || ""}
                        </Badge>
                        :
                        ``
                )
            }
        },

        {
            field: "pocName",
            headerName: "POC",
            width: 140,
            valueGetter: (params) => params?.row?.createdby?.fullName,
            renderCell: (params) => {
                const pocName = params.row?.createdby?.fullName;

                return (
                    <Typography
                        sx={TEXT_BASE_STYLE}
                    >
                        {pocName}
                    </Typography>
                )
            }
        },

        {
            field: "cost",
            headerName: "Total Amount",
            width: 120,
            renderCell: ({ value }) => (
                <Badge
                    sx={{
                        ...BADGE_BASE_STYLE,
                        backgroundColor: "#198754",
                        color: "#FFFFFF",
                        fontWeight: 600
                    }}
                >
                    {`₹${Number(value) || 0}`}
                </Badge>
            )
        },

        {
            field: "gstAmount",
            headerName: "GST Amount",
            width: 100,
            renderCell: ({ value }) => (
                value ?
                    <Badge
                        sx={{
                            ...BADGE_BASE_STYLE,
                            backgroundColor: STATUS_COLOR_MAP.A,
                            color: "#000000",
                            fontWeight: 600
                        }}
                    >
                        {`₹${Number(value) || 0}`}
                    </Badge>
                    :
                    ``
            )
        },

        {
            field: "percentage",
            headerName: "%",
            width: 70,
            renderCell: ({ value }) => (
                <Typography
                    sx={TEXT_BASE_STYLE}
                >
                    {Number(value || 0)}%
                </Typography>
            )
        },

        {
            field: "payableAmount",
            headerName: "Payable Amount",
            width: 130,
            renderCell: ({ value }) => (
                <Badge
                    sx={{
                        ...BADGE_BASE_STYLE,
                        backgroundColor: "#ffc107",
                        color: "#000000",
                        fontWeight: 600
                    }}
                >
                    {`₹${Number(value) || 0}`}
                </Badge>
            )
        },

        {
            field: "status",
            headerName: "Status",
            width: 90,
            renderCell: ({ value }) => (
                <Chip
                    size="small"
                    label={value}
                    sx={{
                        backgroundColor: STATUS_COLOR_MAP[value] || "#dcdcdc",
                        color: "#FFFFFF"
                    }}
                />
            ),
        },

        {
            field: "remarks",
            headerName: "Remarks",
            width: 90,
            renderCell: ({ value }) => (
                <Typography
                    sx={TEXT_BASE_STYLE}
                >
                    {value}
                </Typography>
            )
        },

        {
            field: "actions",
            headerName: "Actions",
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: ({ row }) => {

                const status = row.status;
                const paymentScreenshot = row.paymentScreenshot;
                const isClub = row.isClub;

                let link = paymentScreenshot.startsWith("http") ?
                    paymentScreenshot :
                    process.env.REACT_APP_PAYMENT_PORTAL_BASE + paymentScreenshot;

                return (
                    <Stack
                        columnGap={1}
                    >
                        {
                            status === "P" ?
                                <Stack
                                    direction={"row"}
                                    columnGap={1}
                                    alignItems={'center'}
                                >
                                    {/* Copy Link - visible to everyone */}
                                    <IconButton
                                        size='small'
                                        title='Copy Link'
                                        onClick={() => onCopy(row)}
                                    >
                                        <Copy
                                            size={16}
                                            color={STATUS_COLOR_MAP.W}
                                        />
                                    </IconButton>

                                    {/* Delete - visible to everyone but for only pending status */}
                                    <IconButton
                                        size='small'
                                        title='Delete Link'
                                        onClick={() => onDelete(row)}
                                    >
                                        <Trash2
                                            size={16}
                                            color={STATUS_COLOR_MAP.R}
                                        />
                                    </IconButton>
                                </Stack>
                                :
                                null
                        }

                        {/* View child links - visible to everyone */}
                        {
                            status === "S" ?
                                <Stack
                                    direction={'row'}
                                    columnGap={1}
                                    alignItems={'center'}
                                >
                                    <a
                                        href={link}
                                        target='blank'
                                        rel='noreferrer noopener'
                                    >
                                        <img
                                            src={link}
                                            alt="Payment Screenshot"
                                            width={30}
                                            height={30}
                                            style={{
                                                objectFit: "contain",
                                                borderRadius: 18,
                                                border: "solid 2px #250b1d",
                                                cursor: 'pointer',
                                                fontSize: 12,
                                                verticalAlign: 'middle'
                                            }}
                                            loading="lazy"
                                        />
                                    </a>

                                    <IconButton
                                        size='small'
                                        title='View Grouped Links'
                                        disabled={!isClub}
                                        onClick={() => onViewChildLinks(row)}
                                    >
                                        <Link
                                            size={16}
                                            color={!isClub ? "#6c757d" : STATUS_COLOR_MAP.P}
                                        />
                                    </IconButton>
                                </Stack>
                                :
                                null
                        }
                    </Stack>
                );
            },
        },
    ];